import * as React from 'react';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import ExpandedOrderListCard, { ExtendedOrderListCardLabels } from './ExpandedOrderListCard';
import FlashOnOutlinedIcon from '@mui/icons-material/FlashOnOutlined';
import format from 'date-fns/format';
import AccessTime from '@mui/icons-material/AccessTime';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import { LockOpen, Lock } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import OrderListProductPropertiesIcons from './OrderListProductPropertiesIcons';
import { Order, OrderRow } from '../../BookingsCalendarTypes';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import ViewOrderApi from '../../../view/ViewOrderApi';
import { useBookingOrderListContextMenu } from './state/useBookingsOrderListContextMenu';
import {
    BookingsOrderListContextMenu,
    BookingsOrderListContextMenuLabels
} from './view/BookingsOrderListContextMenu';

type OrderListCardProps = {
    hasMultipleSelection: boolean;
    order: Order;
    labels: OrderListCardLabels;
    activeOrder: number;
    onActiveOrder: (id: number) => void;
    isEven: boolean;
    isLast: boolean;
    onSubmitSplitOrder: (formData: FormData) => Promise<void>;
    onClick: (index: number, event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    isSelected: boolean;
    onClickHideOrders: () => void;
    onClickUnHideOrders: () => void;
    index: number;
};

export type OrderListCardLabels = {
    tooltipClickUnHideOrder: string;
    openOrder: string;
    orderLocked: string;
    lockOrder: string;
    propertyTitle: (value: string) => string;
    brokerName: string;
    brokerFirmName: string;
    images: string;
    express: string;
    photo: string;
    message: string;
    preferredDate: string;
    preferredTimeSlot: string;
    client: string;
    ordered: string;
} & BookingsOrderListContextMenuLabels &
    ExtendedOrderListCardLabels;

function isProductThatCanBeSlitFromOrder(orderRow: OrderRow): boolean {
    if (orderRow.productType === 2) {
        return true;
    }

    if (!orderRow.isPossibleToSplitFromOrder) {
        return false;
    }

    if (orderRow.productType === 3) {
        return true;
    }

    return false;
}

const OrderListCard = ({
    order,
    labels,
    activeOrder,
    onActiveOrder,
    isEven,
    isLast,
    hasMultipleSelection,
    onSubmitSplitOrder,
    onClickHideOrders,
    onClick,
    isSelected,
    index,
    onClickUnHideOrders
}: OrderListCardProps) => {
    const [isOrderLocked, setIsOrderLocked] = React.useState<boolean>(
        !!order.orderListProperties.isLockedBy.name
    );
    const [isSplitting, setIsSplitting] = React.useState<boolean>(false);

    const [assignedUserName, setAssignedUserName] = React.useState(
        order.orderListProperties.isLockedBy.name || null
    );

    function handleSubmitSplitOrder(formData: FormData) {
        setIsSplitting(true);
        onSubmitSplitOrder(formData).then(() => {
            setIsSplitting(false);
        });
    }

    const handleExpandClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        onClick(index, event);

        if (event.shiftKey || event.ctrlKey) {
            return;
        }

        let target = event.target as HTMLElement;
        if (target.tagName !== 'path' && target.tagName !== 'svg') {
            if (!activeOrder || order.orderId !== activeOrder) {
                onActiveOrder(order.orderId);
            }
            if (order.orderId === activeOrder) {
                onActiveOrder(0);
            }
        }
    };

    let { orderListCardRef, onCloseContextMenu, contextMenuPosition } =
        useBookingOrderListContextMenu();

    let StyledTypography = styled(Typography)(() => ({
        fontFamily: '"Roboto"',
        fontSize: '9px',
        fontWeight: '400',
        textAlign: 'left',
        color: 'rgba(0, 0, 0, 0.87)'
    }));

    let InfoTooltipText = styled(Typography)(() => ({
        fontSize: '10px'
    }));

    const getBackgroundColor = () => {
        if (isSelected) {
            return '#EFF7FE';
        }
        if (order.orderDeliveryProperties.isExpress) {
            return '#FCECEB';
        }
        if (isEven) {
            return '';
        } else {
            return '#FAFAFA';
        }
    };
    const getHoverBackgroundColor = () => {
        if (isSelected) {
            return '#D6E9FC';
        }
        if (order.orderDeliveryProperties.isExpress) {
            return '#FADAD8';
        }
        return '#EFF0F1';
    };
    const handleOrderLock = async () => {
        let assignedUser = await ViewOrderApi.assignUser(order.orderId);
        setAssignedUserName(assignedUser.assignedUserName);
        setIsOrderLocked(!isOrderLocked);
    };

    return (
        <>
            <Card
                ref={orderListCardRef}
                onClick={handleExpandClick}
                variant="outlined"
                sx={{
                    userSelect: 'none',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: getHoverBackgroundColor() },
                    border: isSelected ? '1px solid rgba(66, 66, 66, 1)' : '1px solid transparent',
                    borderRadius: isSelected ? '2px' : '0',
                    borderBottomLeftRadius: isLast ? '4px' : '0',
                    borderBottomRightRadius: isLast ? '4px' : '0',
                    backgroundColor: getBackgroundColor()
                }}
            >
                <CardContent sx={{ padding: '10px !important' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            width: '100%',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'start',
                                minWidth: '85px'
                            }}
                        >
                            <Link
                                to={`/orders/${order.orderId}/basic`}
                                target="_blank"
                                style={{ display: 'flex' }}
                            >
                                <Tooltip title={labels.openOrder}>
                                    <Typography
                                        variant="h5"
                                        aria-label="card-heading"
                                        sx={{
                                            fontFamily: '"Roboto"',
                                            fontWeight: '700',
                                            lineHeight: '150%',
                                            fontSize: '11px',
                                            color: '#4F7C6D',
                                            marginRight: '5px'
                                        }}
                                    >
                                        {order.orderCollectionNumber + ':' + order.orderNumber}
                                    </Typography>
                                </Tooltip>
                            </Link>
                            <StyledTypography variant="body2">
                                {format(new Date(order.createdAt), 'dd/MM/yyyy, HH:mm')}
                            </StyledTypography>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'start',
                                flex: isSplitting ? 'unset' : '1 auto'
                            }}
                        >
                            <StyledTypography
                                variant="body2"
                                sx={{ fontWeight: 'bold', fontSize: '11px' }}
                            >
                                {order.orderAddress.address}
                            </StyledTypography>
                            <StyledTypography variant="body2">
                                {order.orderAddress.city}
                            </StyledTypography>
                        </Box>
                        {isSplitting ? (
                            <LinearProgress
                                sx={{ width: '100px', flex: '1 auto', margin: '0 10%' }}
                                variant="query"
                            />
                        ) : null}
                        <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'default' }}>
                            <OrderListProductPropertiesIcons order={order} labels={labels} />
                            {order.orderDeliveryProperties.isExpress ? (
                                <Tooltip title={labels.express}>
                                    <FlashOnOutlinedIcon
                                        viewBox="0 0 28 24"
                                        sx={{
                                            fill: '#E31B0C',
                                            cursor: 'default',
                                            marginRight: '-5px'
                                        }}
                                    />
                                </Tooltip>
                            ) : null}
                            <Divider
                                orientation="vertical"
                                variant="middle"
                                flexItem
                                sx={{ margin: '5px', borderColor: '#0003' }}
                            />
                            {order.preferredDate ? (
                                <Tooltip
                                    title={
                                        <Box>
                                            <InfoTooltipText>
                                                {format(
                                                    new Date(order.preferredDate),
                                                    'dd/MM/yyyy'
                                                )}
                                                {order.preferredTimeSlot
                                                    ? ' ' + order.preferredTimeSlot
                                                    : ''}
                                            </InfoTooltipText>
                                        </Box>
                                    }
                                >
                                    <AccessTime sx={{ fill: '#333D45', cursor: 'default' }} />
                                </Tooltip>
                            ) : null}
                            {order.isHidden ? (
                                <Tooltip title={labels.tooltipClickUnHideOrder}>
                                    <IconButton
                                        sx={{ padding: '4px', cursor: 'pointer' }}
                                        onClick={onClickUnHideOrders}
                                    >
                                        <VisibilityOffIcon />
                                    </IconButton>
                                </Tooltip>
                            ) : null}
                            <IconButton
                                onClick={() => {
                                    handleOrderLock();
                                }}
                                sx={{
                                    padding: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                {isOrderLocked ? (
                                    <Tooltip title={labels.orderLocked + ' ' + assignedUserName}>
                                        <Lock
                                            sx={{
                                                color: '#333D45'
                                            }}
                                        />
                                    </Tooltip>
                                ) : (
                                    <Tooltip title={labels.lockOrder}>
                                        <LockOpen
                                            sx={{
                                                color: '#333D45'
                                            }}
                                        />
                                    </Tooltip>
                                )}
                            </IconButton>
                        </Box>
                    </Box>
                </CardContent>
                <Collapse in={order.orderId === activeOrder}>
                    <ExpandedOrderListCard order={order} labels={labels} />
                </Collapse>
            </Card>
            {order.orderId === activeOrder ? null : <Divider />}
            <BookingsOrderListContextMenu
                onSubmitSplitOrder={handleSubmitSplitOrder}
                orderId={order.orderId}
                hasMultipleSelection={hasMultipleSelection}
                isOrderHidden={order.isHidden}
                products={order.orderRows.filter(isProductThatCanBeSlitFromOrder)}
                onClose={onCloseContextMenu}
                onClickHideOrders={onClickHideOrders}
                onClickUnHideOrders={onClickUnHideOrders}
                position={contextMenuPosition}
                labels={{
                    splitOrder: labels.splitOrder,
                    cloneOrder: labels.cloneOrder,
                    goToOrder: labels.goToOrder,
                    hideOrders: labels.hideOrders,
                    description: labels.description,
                    submitButton: labels.submitButton,
                    canNotSplitOnNoProducts: labels.canNotSplitOnNoProducts,
                    canNotSplitAllProducts: labels.canNotSplitAllProducts,
                    mainProductTitle: labels.mainProductTitle,
                    showOrders: labels.showOrders
                }}
            />
        </>
    );
};

export default OrderListCard;
